import { loan_quote_presentation_api } from '@axo/shared/data-access/api';
import { useAPI } from '@axo/shared/data-access/provider';
import type { loan_application } from '@axo/shared/data-access/types';
import {
  type UseMutationOptions,
  type UseQueryOptions,
  useMutation,
  useQuery,
} from 'react-query';
import {
  applicationKeys,
  parseApplicationKey,
} from '../../cache-keys/applicationKeys';
import { queryFunction } from '../../query-function';

export type ProductRecommendationData = Awaited<
  ReturnType<
    ReturnType<typeof loan_quote_presentation_api.getProductRecommendation>
  >
>;
export type ProductRecommendationQueryKey = ReturnType<
  typeof applicationKeys.productRecommendation
>;

export type UseProductRecommendationParams = {
  applicationId: string | undefined;
};

export type UseProductRecommendationOptions = Pick<
  UseQueryOptions<
    ProductRecommendationData,
    Error,
    ProductRecommendationData,
    ProductRecommendationQueryKey
  >,
  'enabled' | 'onSuccess' | 'onError'
> & {
  retryUntilCompleted?: boolean;
};

export function useGetProductRecommendation(
  params: UseProductRecommendationParams,
  {
    retryUntilCompleted = true,
    enabled = true,
    ...options
  }: UseProductRecommendationOptions = {}
) {
  const api = useAPI();

  const cacheKey = applicationKeys.productRecommendation({
    applicationID: params.applicationId,
  });

  return useQuery({
    queryKey: cacheKey,
    queryFn: queryFunction<
      ProductRecommendationQueryKey,
      string,
      ProductRecommendationData
    >(
      loan_quote_presentation_api.getProductRecommendation(api),
      ({ queryKey }) => parseApplicationKey(queryKey).applicationID ?? ''
    ),
    // refetch every second until we have CompletedAt
    refetchInterval: (data) =>
      retryUntilCompleted && !data?.CompletedAt ? 1_000 : false,
    ...options,
    enabled: !!api.token && !!params.applicationId && enabled,
  });
}

// ─── Product Recommendation Reapply ───

export type ProductRecommendationReapplyParams = {
  applicationId: string;
  product: loan_application.Product;
  channels?: loan_application.ChannelSnapshot[];
  appliedAmount?: number;
};

export type ProductRecommendationReapplyData = loan_application.LoanApplication;

export type UseProductRecommendationReapplyOptions = Pick<
  UseMutationOptions<
    ProductRecommendationReapplyData,
    Error,
    ProductRecommendationReapplyParams
  >,
  'onSuccess' | 'onError'
>;

/**
 * Hook to reapply for a recommendation with a specific product.
 * Creates a new loan application cloned from the original with the selected product.
 */
export function useProductRecommendationReapply(
  options: UseProductRecommendationReapplyOptions = {}
) {
  const api = useAPI();

  return useMutation({
    mutationFn: ({
      applicationId,
      product,
      channels,
      appliedAmount,
    }: ProductRecommendationReapplyParams) =>
      loan_quote_presentation_api.postProductRecommendationReapply(api)(
        { Product: product, Channels: channels, AppliedAmount: appliedAmount },
        { applicationId },
      ),
    ...options,
  });
}
